import memoize from 'lodash/memoize';
import trimStart from 'lodash/trimStart';
import { parsePhoneNumber, AsYouType } from 'libphonenumber-js';

export const phoneFormat = "(555) 555-5555'";
const E164 = 'E.164';
const NATIONAL = 'NATIONAL';
const INTERNATIONAL = 'INTERNATIONAL';
const URI = 'RFC3966';

const FIXED_LINE_OR_MOBILE = 'FIXED_LINE_OR_MOBILE';

export const formats = { E164, INTERNATIONAL, NATIONAL, URI };

const ALLOWED_FORMATS = new Set(Object.values(formats));

const UNTEXTABLE_TYPES = [
  'TOLL_FREE',
  'PAGER',
  'UAN',
  'VOICE_MAIL',
  'FIXED_LINE',
];

export function isLandlineOrMobile(parsedPhone) {
  try {
    const type = parsePhoneNumber(parsedPhone).getType();
    return type === FIXED_LINE_OR_MOBILE;
  } catch (e) {
    return false;
  }
}

export function isUntextable(parsedPhone) {
  try {
    const type = parsePhoneNumber(parsedPhone).getType();
    return UNTEXTABLE_TYPES.includes(type);
  } catch (e) {
    return null;
  }
}

export const getCountry = memoize((phone) => {
  if (!phone) return 'US';
  try {
    return parsePhoneNumber(phone).country;
  } catch (e) {
    return 'US';
  }
});

const asYouType = memoize((reference) => new AsYouType(getCountry(reference)));

export function parsePhone(phone, referenceNumber) {
  try {
    const country = getCountry(referenceNumber);
    const parsedNumber = parsePhoneNumber(phone, country);

    if (phone.startsWith(parsedNumber.countryCallingCode)) {
      return parsePhoneNumber(
        trimStart(phone, parsedNumber.countryCallingCode),
        country
      );
    }

    return parsePhoneNumber(phone, country);
  } catch (e) {
    return {};
  }
}

export function validatePhone(phone, referenceNumber) {
  try {
    const country = getCountry(referenceNumber);
    return parsePhoneNumber(phone, country).isValid();
  } catch (e) {
    return false;
  }
}

export function maskPhone(phone, referenceNumber = '+1 888 111 2222') {
  /**
   * as of 1.7.32, asYouType().input() cannot accept null.
   * See: https://github.com/catamphetamine/libphonenumber-js/issues/383
   */
  if (!phone) return;
  /**
   * libphonenumber's implementation doesn't allow deleting the right parenthesis
   * aka when deleting from 5 to 4 characters. Hence the hack for 4 chars.
   */
  if (phone && phone.length === 4) return phone;
  asYouType(referenceNumber).reset();
  return asYouType(referenceNumber).input(phone);
}

export function formatPhone(phone, referenceNumberOrFormat) {
  let format;
  let referenceNumber;

  if (ALLOWED_FORMATS.has(referenceNumberOrFormat)) {
    referenceNumber = phone;
    format = referenceNumberOrFormat;
  } else {
    referenceNumber = referenceNumberOrFormat;
    format = E164;
  }

  try {
    const parsedNumber = parsePhone(phone, referenceNumber);
    return parsedNumber.format ? parsedNumber.format(format) : phone;
  } catch (e) {
    return phone;
  }
}
